import { useEffect, useRef } from "react";

// react-router components
import { Routes, Route, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Smooth scrolling
import Lenis from "lenis";
import "lenis/dist/lenis.css";

// Material Kit 2 React themes
import theme from "assets/theme";

// Material Kit 2 React routes
import routes from "routes";

// Pages not in navbar
import Payment from "layouts/pages/payment";
import Admin from "layouts/pages/admin";
import SeptemberOffer from "layouts/pages/september-offer";
import NotFound from "pages/NotFound";

export default function App() {
  const { pathname, search } = useLocation();
  const lenisRef = useRef(null);

  // Smooth (inertia) scrolling across the whole site so a stepped mouse wheel
  // still glides. respectReducedMotion:false keeps it on even when the OS has
  // "reduce motion" enabled — Lenis 1.3.26+ turns smoothing off under that
  // preference by default, which we override to keep the effect site-wide.
  useEffect(() => {
    const lenis = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
      allowNestedScroll: true,
      respectReducedMotion: false,
    });
    lenisRef.current = lenis;

    let rafId = requestAnimationFrame(function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    });

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Setting page scroll to 0 when changing the route (instant, even with Lenis on)
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      document.documentElement.scrollTop = 0;
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  // Send a GA4 page_view on each client-side route change (SPA navigation).
  useEffect(() => {
    if (typeof window.gtag !== "function") return undefined;
    // Defer so react-helmet-async has applied the new document.title first.
    const timer = window.setTimeout(() => {
      window.gtag("event", "page_view", {
        page_path: `${pathname}${search}`,
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [pathname, search]);

  // Send a Meta Pixel PageView on each client-side route change (SPA navigation).
  useEffect(() => {
    if (typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [pathname, search]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.route} />;
      }

      return null;
    });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {getRoutes(routes)}
        <Route path="/payment/:bookingId" element={<Payment />} />
        <Route path="/september-offer" element={<SeptemberOffer />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ThemeProvider>
  );
}
